import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { customerService } from '../../../services/api';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import ErrorState from '../../../components/dashboard/ErrorState';
import PermissionDenied from '../../../components/dashboard/PermissionDenied';
import { formatDate } from '../../../utils/formatters';

export default function LeadOriginDetail() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const leadOriginQuery = useQuery({
    queryKey: ['leadOrigin', id],
    queryFn: () => customerService.getLeadOrigin(id).then((res) => res.data),
  });

  const leadOrigin = leadOriginQuery.data;

  // Deletes straight away and lets the list reopen the undo snackbar via
  // `seedDelete` — the backend soft-deletes, so "Desfazer" there can still
  // restore this exact row (uuid and lead history included).
  const handleDelete = async () => {
    await customerService.deleteLeadOrigin(id);
    queryClient.invalidateQueries({ queryKey: ['leadOrigins'] });
    navigate('/lead-origins', {
      state: { seedDelete: { ids: [id], message: t('leadOrigins.deleteSuccess') } },
    });
  };

  const header = (
    <PageHeader
      title={leadOrigin?.name ?? t('leadOrigins.detailTitle')}
      breadcrumbs={[
        { label: t('leadOrigins.breadcrumb.dashboard') },
        { label: t('nav.groups.settings') },
        { label: t('leadOrigins.breadcrumb.list'), to: '/lead-origins' },
        { label: leadOrigin?.name ?? t('leadOrigins.detailTitle') },
      ]}
    />
  );

  const renderBody = () => {
    if (leadOriginQuery.isLoading) {
      return <LoadingState message={t('leadOrigins.states.loading')} />;
    }

    if (leadOriginQuery.isError) {
      const status = leadOriginQuery.error?.response?.status;
      if (status === 403) {
        return (
          <PermissionDenied
            title={t('leadOrigins.states.permissionDeniedTitle')}
            message={t('leadOrigins.states.permissionDeniedMessage')}
          />
        );
      }
      if (status === 404) {
        return (
          <ErrorState
            title={t('leadOrigins.states.notFoundTitle')}
            message={t('leadOrigins.states.notFoundMessage')}
          />
        );
      }
      return (
        <ErrorState
          title={t('leadOrigins.states.errorTitle')}
          message={t('leadOrigins.states.errorMessage')}
          actionLabel={t('leadOrigins.states.retry')}
          onRetry={leadOriginQuery.refetch}
        />
      );
    }

    return (
      <Stack spacing={3} sx={{ width: '100%' }}>
        <Card>
          <CardHeader
            title={t('leadOrigins.sections.identification')}
            subheader={t('leadOrigins.sections.identificationHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent sx={{ py: 0 }}>
            <List disablePadding>
              <ListItem disableGutters divider>
                <ListItemText
                  primary={t('leadOrigins.fields.name')}
                  secondary={leadOrigin.name}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                />
              </ListItem>
              <ListItem disableGutters divider>
                <ListItemText
                  primary={t('leadOrigins.fields.triggerPhrase')}
                  secondary={leadOrigin.trigger_phrase || '—'}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                />
              </ListItem>
              <ListItem disableGutters divider>
                <ListItemText
                  primary={t('leadOrigins.fields.leadCount')}
                  secondary={leadOrigin.lead_count ?? 0}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                />
              </ListItem>
              <ListItem disableGutters divider>
                <ListItemText
                  primary={t('leadOrigins.fields.createdAt')}
                  secondary={formatDate(leadOrigin.created_at, i18n.language)}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                />
              </ListItem>
              <ListItem disableGutters>
                <ListItemText
                  primary={t('leadOrigins.fields.updatedAt')}
                  secondary={formatDate(leadOrigin.updated_at, i18n.language)}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                  secondaryTypographyProps={{ variant: 'body1', color: 'text.primary' }}
                />
              </ListItem>
            </List>
          </CardContent>
          <Divider />
          <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
            <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDelete}>
              {t('leadOrigins.actions.delete')}
            </Button>
            <Button
              variant="contained"
              startIcon={<EditOutlinedIcon />}
              onClick={() => navigate(`/lead-origins/${id}/edit`)}
            >
              {t('leadOrigins.actions.edit')}
            </Button>
          </CardActions>
        </Card>
      </Stack>
    );
  };

  return <BoundedPageLayout header={header}>{renderBody()}</BoundedPageLayout>;
}
